
import { Injectable } from '@angular/core';
import { NGXLogger as LoggerService } from "ngx-logger";
import { LOCALE_ID, Inject } from '@angular/core';
import { CurrentPollService } from './current-poll.service';
import { AuthService } from '../../user/auth.service';
import { paths } from 'functions-lib';
import { EssErrorService } from 'ngx-essentia';
@Injectable({
    providedIn: 'root'
})
/**
 * Service that holds the current ownerId and pollId which are set with initService()
 * Components can use changeIndicator to get informed if the current poll has changed
 * Use this service to get the paths to the DB and the URLs
 */
export class PathService {

    constructor(
        private logger: LoggerService,
        @Inject(LOCALE_ID) public locale: string,
        private authService: AuthService,
        private currentPollService: CurrentPollService,
        private errorService: EssErrorService
    ) {
    }

    /**
     * Returns something like ${PATH_TO_OPTIONS}/${this.ownerId}/${this.pollId
     */
    public getOptionsPath(ownerId?: string, pollId?: string): string {
        this.assertPollContext('getOptionsPath', ownerId, pollId);
        return `${paths.options}/${this.currentPollService.getOwnerId(ownerId)}/${this.currentPollService.getPollId(pollId)}`;
    }


    public getOptionsArrayPath(ownerId?: string, pollId?: string): string {
        this.assertPollContext('getOptionsArrayPath', ownerId, pollId);
        return `${paths.options}/${this.currentPollService.getOwnerId(ownerId)}/${this.currentPollService.getPollId(pollId)}/${paths.optionArray}`;
    }


    public getOptionsSortMapPath(ownerId?: string, pollId?: string): string {
        this.assertPollContext('getOptionsSortMapPath', ownerId, pollId);
        return `${paths.options}/${this.currentPollService.getOwnerId(ownerId)}/${this.currentPollService.getPollId(pollId)}/${paths.optionSortMap}`;
    }

    /**
     * Throws when the ids are neither passed nor available from an initialized poll -
     * a path like options/null/null must never be read from or written to
     */
    private assertPollContext(caller: string, ownerId?: string, pollId?: string): void {
        if ((!ownerId || !pollId) && !this.currentPollService.isInitialized) {
            throw this.errorService.newError(
                `${caller}: poll not initialized - ownerId: ${ownerId ?? 'none'}, pollId: ${pollId ?? 'none'}`);
        }
    }

    public getaccessLinkNotificationPath(): string {
        return paths.accessLinkNotification.toBeProcessed
    }

    public getfeedbackNotificationPath(): string {
        return paths.feedbackNotification.toBeProcessed
    }
    public getCurrentTimePath(): string {
        return paths.currentTime
    }
    /**
     * Returns something like ${PATH_TO_SURVEY}/${ownerId}/${pollId}
     */
    public getHeaderPath(ownerId?: string, pollId?: string): string {
        this.logger.log('getHeaderPath: ', ownerId, pollId);
        return paths.header + '/' + this.currentPollService.getOwnerId(ownerId) + '/' + this.currentPollService.getPollId(pollId);
    }

    /**
     * Returns something like ${PATH_TO_VOTES}/${ownerId}/${pollId}
     */
    public getVotesPath(ownerId?: string, pollId?: string): string {
        return paths.votes + '/' + this.currentPollService.getOwnerId(ownerId) + '/' + this.currentPollService.getPollId(pollId);
    }

    public getUserVotePath(userId: string, ownerId?: string, pollId?: string): string {
        this.logger.log('getUserVotePath: userId: ' + userId);
        return paths.votes + '/' + this.currentPollService.getOwnerId(ownerId) + '/' + this.currentPollService.getPollId(pollId) + '/' + userId;
    }

    /**
     * Returns something like ${PATH_TO_VOTE_PRIVATE}/${ownerId}/${pollId}
     * (the access restricted contact part of the votes, see paths.votePrivate)
     */
    public getVotePrivatePath(ownerId?: string, pollId?: string): string {
        return paths.votePrivate + '/' + this.currentPollService.getOwnerId(ownerId) + '/' + this.currentPollService.getPollId(pollId);
    }

    public getUserVotePrivatePath(userId: string, ownerId?: string, pollId?: string): string {
        return this.getVotePrivatePath(ownerId, pollId) + '/' + userId;
    }

    /**
     * Returns something like ${PATH_TO_VOTE_COUNTS}/${ownerId}/${pollId}
     * (the public per-option aggregates maintained by the votesCounter trigger)
     */
    public getVoteCountsPath(ownerId?: string, pollId?: string): string {
        return paths.voteCounts + '/' + this.currentPollService.getOwnerId(ownerId) + '/' + this.currentPollService.getPollId(pollId);
    }

    public getSurveyParticipatedPath(ownerId: string, pollId: string, voteUserId: string): string {
        return paths.participation + '/' + voteUserId + '/' + ownerId + '-' + pollId;
    }

    /**
     * Returns something like "surveys-header/eQjTCpFdYTPNjYC5MsKZwM5Lqvx1"
     * @param ownerId
     */
    public getPollsOwnedPath(ownerId?: string): string {
        return paths.header + '/' + this.currentPollService.getOwnerId(ownerId);
    }

    public getSurveysParticipatedPath(uid?): string {
        return paths.participation + '/' + this.getUid(uid);
    }

    private getUid(uid?: string): string {
        return uid ? uid : this.authService.uid;
    }

}
